import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { siteConfig, navLinks, initiativeLinks } from "../data/mock";
import { ChevronDown, Menu, X } from "lucide-react";

const ADNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropOpen(false);
  }, [location]);

  const isInitiativesActive = location.pathname.startsWith("/initiatives");

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white border-b border-gray-100" : "bg-white"
        }`}
        style={{ height: "64px" }}
      >
        <div className="max-w-6xl mx-auto px-6 h-full flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="text-base font-semibold tracking-[0.2em] text-gray-900"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "0.15em" }}
          >
            {siteConfig.name}
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-wide transition-colors duration-200 ${
                  location.pathname === link.path ? "text-gray-900" : "text-gray-400 hover:text-gray-900"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Initiatives dropdown */}
            <div className="relative" ref={dropRef}>
              <button
                onClick={() => setDropOpen(!dropOpen)}
                className={`flex items-center gap-1 text-sm tracking-wide transition-colors duration-200 ${
                  isInitiativesActive ? "text-gray-900" : "text-gray-400 hover:text-gray-900"
                }`}
              >
                Initiatives
                <ChevronDown size={13} className={`transition-transform duration-200 ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              {dropOpen && (
                <div className="absolute right-0 top-full mt-3 w-72 bg-white rounded shadow-lg border border-gray-100 py-2 z-50">
                  {initiativeLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-5 py-2.5 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                      onClick={() => setDropOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden text-gray-700" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-white pt-16 md:hidden">
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} className="block py-3 text-sm text-gray-600 border-b border-gray-50 tracking-wide">
                {link.label}
              </Link>
            ))}
            <div className="pt-3 pb-1 text-[10px] font-medium text-gray-400 uppercase tracking-[0.2em]">Initiatives</div>
            {initiativeLinks.map((link) => (
              <Link key={link.path} to={link.path} className="block py-3 text-sm text-gray-500 border-b border-gray-50 tracking-wide pl-3">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ADNavbar;
